// M145 — Nieuwe Overzicht (home). Match met referentie-ontwerp.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  Users, Euro, MousePointerClick, ArrowRight, Lightbulb, Target, Palette,
  ChevronDown, Sparkles,
} from "lucide-react";

const PERIODS = [
  { key: "7d", label: "Laatste 7 dagen" },
  { key: "30d", label: "Laatste 30 dagen" },
  { key: "mo", label: "Deze maand" },
  { key: "prev-mo", label: "Vorige maand" },
];

function greetPrefix() {
  const h = new Date().getHours();
  if (h >= 6  && h < 12) return "Goedemorgen";
  if (h >= 12 && h < 18) return "Goedemiddag";
  if (h >= 18 && h < 23) return "Goedenavond";
  return "Welkom terug";
}

function firstName(user) {
  const raw = (user?.first_name || user?.name || "").trim();
  if (!raw) return "";
  const first = raw.split(/\s+/)[0];
  if (!first || first.includes("@")) return "";
  // Skip all-caps korte afkortingen (waarschijnlijk org-code zoals NLV,
  // ABC, NLV-Test) — dat is geen echte voornaam.
  if (/^[A-Z0-9]{2,5}([-_].*)?$/.test(first)) return "";
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

function KpiCard({ icon: Icon, eyebrow, value, subline, subhref, tone = "empty", testid }) {
  const nav = useNavigate();
  return (
    <div className="bg-bg-panel rounded-[16px] p-6 shadow-card"
          style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
          data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-accent-mint flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-accent-teal" style={{ letterSpacing: "0.06em" }}>
            {eyebrow}
          </div>
          <div className="mt-1">
            {tone === "empty" ? (
              <div className="text-3xl font-light text-text-muted">—</div>
            ) : (
              <div className="text-3xl font-bold text-text-primary">{value}</div>
            )}
          </div>
          <button onClick={() => subhref && nav(subhref)}
                   className="mt-2 text-sm text-text-muted hover:text-accent-teal-dim text-left">
            {subline}
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceIcon({ type }) {
  const Map = {
    seo_advies: Target,
    cro_advies: Lightbulb,
    brand_advies: Palette,
    vlog_kans: Sparkles,
    post_kans: Sparkles,
  };
  const I = Map[type] || Lightbulb;
  return <I className="w-5 h-5 text-text-primary" />;
}

function ProposalCard({ p, index }) {
  const nav = useNavigate();
  return (
    <div className="bg-bg-panel rounded-[16px] p-6 shadow-card relative"
          style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}
          data-testid={`overzicht-proposal-${index}`}>
      {p.is_example && (
        <span className="inline-block rounded-full text-[11px] font-semibold text-text-primary mb-3"
               style={{ background: "#F4B183", padding: "4px 10px", letterSpacing: "0.08em" }}>
          VOORBEELD
        </span>
      )}
      <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center mb-4">
        <SourceIcon type={p.source_type} />
      </div>
      <h3 className="text-base font-bold text-text-primary mb-2 leading-snug">{p.title}</h3>
      <p className="text-sm text-text-muted mb-4"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {p.description}
      </p>
      <button onClick={() => nav(p.cta_route)}
               className="text-sm font-medium text-accent-teal-dim hover:text-accent-teal inline-flex items-center gap-1"
               data-testid={`overzicht-proposal-${index}-open`}>
        {p.cta_text || "Bekijk voorstel"} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function OverzichtPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [periodOpen, setPeriodOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/overzicht/voorstellen?limit=3")
      .then((r) => setData(r.data))
      .catch(() => setErr("Kon voorstellen niet laden."));
  }, []);

  const heroTitle = useMemo(() => {
    if (!data) return "Even geduld, we laden je overzicht…";
    if ((data.proposals || []).length === 0) return "Eerst overzicht. Dan de volgende stap.";
    return `Deze week ${data.open_count} concrete ${data.open_count === 1 ? "kans" : "kansen"} om op te pakken.`;
  }, [data]);

  const heroSub = data && (data.proposals || []).length === 0
    ? "Bekijk je kansen en bepaal zelf wat je toepast."
    : "Rangschikking op wat het meest oplevert. Klik door voor detail.";

  const proposals = (data?.proposals || []).slice(0, 3);

  return (
    <AppLayout>
      {/* Header — eyebrow + naam + subtitel */}
      <div className="pt-2">
        <div className="text-xs font-semibold text-accent-teal"
             style={{ letterSpacing: "0.06em" }} data-testid="overzicht-eyebrow">
          JOUW MARKETINGOVERZICHT
        </div>
        <h1 className="mt-2 text-4xl md:text-5xl font-bold text-text-primary tracking-tight"
             data-testid="overzicht-greeting">
          {(() => {
            const prefix = greetPrefix();
            const name = firstName(user);
            return name ? `${prefix}, ${name}` : prefix;
          })()}
        </h1>
        <p className="mt-3 text-lg text-text-muted max-w-2xl">
          Dit verdient vandaag jouw aandacht.
        </p>
      </div>

      {/* Sectie 1 — Mint hero */}
      <div className="mt-8 rounded-[16px] p-8 bg-accent-mint flex items-center justify-between gap-6 flex-wrap"
            data-testid="overzicht-mint-hero">
        <div className="flex-1 min-w-[280px]">
          <h2 className="text-2xl font-bold text-text-primary leading-tight">
            {heroTitle}
          </h2>
          <p className="mt-2 text-base text-text-primary/70">
            {heroSub}
          </p>
        </div>
        <button
          onClick={() => nav("/mijn-weekplan")}
          data-testid="overzicht-open-weekplan"
          className="inline-flex items-center gap-2 rounded-[12px] px-6 py-3.5 bg-accent-teal text-white font-medium hover:bg-accent-teal-dim transition-colors"
          style={{ fontSize: 15, boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
        >
          Bekijk mijn weekplan <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Sectie 2 — Periodekeuze */}
      <div className="mt-6 flex justify-end relative">
        <button
          onClick={() => setPeriodOpen(v => !v)}
          data-testid="overzicht-period-toggle"
          className="inline-flex items-center gap-2 rounded-[12px] bg-bg-panel border border-border-hairline px-5 py-3 text-sm font-medium text-text-primary hover:bg-bg-panel-raised"
        >
          {PERIODS.find(p => p.key === period).label}
          <ChevronDown className="w-4 h-4 text-text-muted" />
        </button>
        {periodOpen && (
          <div className="absolute right-0 top-14 z-20 min-w-[200px] rounded-md border border-border-hairline bg-bg-panel shadow-popover">
            {PERIODS.map((p) => (
              <button key={p.key}
                       onClick={() => { setPeriod(p.key); setPeriodOpen(false); }}
                       className={`block w-full text-left px-4 py-2 text-sm ${p.key === period ? "text-accent-teal" : "text-text-primary"} hover:bg-bg-canvas`}>
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sectie 3 — KPI-strip */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <KpiCard icon={Users} eyebrow="AANVRAGEN" tone="empty"
                 subline="Koppel je kanalen" subhref="/kanalen"
                 testid="overzicht-kpi-aanvragen" />
        <KpiCard icon={Euro} eyebrow="ADVERTENTIEKOSTEN" tone="empty"
                 subline="Koppel je kanalen" subhref="/kanalen"
                 testid="overzicht-kpi-adkosten" />
        <KpiCard icon={MousePointerClick} eyebrow="WEBSITEBEZOEKEN" tone="empty"
                 subline="Wacht op activatie GA4" subhref="/kanalen"
                 testid="overzicht-kpi-bezoeken" />
      </div>

      {/* Sectie 4 — Klaar voor jouw beoordeling */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl font-bold text-text-primary">Klaar voor jouw beoordeling</h2>
        </div>
        <div className="border-b border-border-hairline mb-6" />
        {err ? (
          <div className="text-sm text-warn-amber">{err}</div>
        ) : proposals.length === 0 ? (
          <div className="rounded-[16px] border border-dashed border-border-hairline bg-bg-panel p-8 text-center"
                data-testid="overzicht-proposals-empty">
            <p className="text-sm text-text-muted max-w-md mx-auto mb-4">
              Zodra je een kanaal koppelt of je site laat analyseren, verschijnen hier voorstellen.
            </p>
            <button onClick={() => nav("/kanalen")}
                     className="inline-flex items-center gap-2 rounded-[12px] px-5 py-2.5 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim">
              Koppel een kanaal <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="overzicht-proposals-grid">
            {proposals.map((p, i) => (<ProposalCard key={p.id || i} p={p} index={i} />))}
          </div>
        )}
      </div>

      {/* Sectie 5 — Ghost mode uitleg */}
      <div className="mt-10 border-t border-border-hairline pt-6">
        <p className="text-sm text-text-muted flex items-center gap-2"
            data-testid="overzicht-ghost-explain">
          <span className="inline-block w-2 h-2 rounded-full bg-pos-green" />
          We analyseren en adviseren. In Ghost Mode wijzigen of publiceren we niets.
        </p>
      </div>
    </AppLayout>
  );
}
